// Shared viewport / off-screen helpers for TouchTour Studio.
//
// These project a node's world (grid) coordinates into canvas screen space using
// the exact same maths as TourCanvas.getPosition, but driven by a camera read
// from the Canvas API (getCameraState) so overlays never reach into TourCanvas
// internals. Used by WP-F (EditorAlerts "scenes outside view") and WP-H
// (OffscreenIndicators edge arrows) so the two stay in lock-step.

import { useEffect, useRef, useState } from "react";

import { gridUnit } from "../TourCanvas";

// Project a node's world coords to screen space — mirrors TourCanvas.getPosition.
// Returns null until the canvas has been measured (no rect yet).
export const projectNode = (image, camera) => {
  const rect = camera?.canvasRect;
  if (!rect || !rect.width || !rect.height) return null;

  const left = (image.x + camera.x) * gridUnit * camera.scale + rect.width / 2;
  const top = -(image.y + camera.y) * gridUnit * camera.scale + rect.height / 2;
  return { left, top, width: rect.width, height: rect.height };
};

// True when the node's projected position falls outside the visible canvas.
export const isOffscreen = (image, camera) => {
  const p = projectNode(image, camera);
  if (!p) return false;
  return p.left < 0 || p.left > p.width || p.top < 0 || p.top > p.height;
};

// For an off-screen node, returns where to draw an edge indicator and which way
// it should point:
//   - left/top: the node's projected point clamped to a margin inside the canvas
//     edges (so the chevron hugs the border nearest the node),
//   - angle: radians from the canvas centre toward the node. Screen space is
//     y-down and CSS `rotate` is clockwise-positive, so this angle can be fed
//     straight into a `rotate(${angle}rad)` on a right-pointing chevron.
// Returns null when the node is on-screen.
export const getEdgeIndicator = (image, camera, margin = 28) => {
  const p = projectNode(image, camera);
  if (!p) return null;

  const onScreen =
    p.left >= 0 && p.left <= p.width && p.top >= 0 && p.top <= p.height;
  if (onScreen) return null;

  const cx = p.width / 2;
  const cy = p.height / 2;
  const angle = Math.atan2(p.top - cy, p.left - cx);

  const left = Math.max(margin, Math.min(p.width - margin, p.left));
  const top = Math.max(margin, Math.min(p.height - margin, p.top));

  return { left, top, angle };
};

const rectChanged = (a, b) =>
  (a && a.width) !== (b && b.width) ||
  (a && a.x) !== (b && b.x) ||
  (a && a.y) !== (b && b.y);

// Subscribe to the live camera from the Canvas API. The API has no change
// events, so we poll a snapshot each frame and only re-render when something
// that affects node projection actually moved (pan, zoom, or canvas resize).
export const useLiveCamera = canvasApiRef => {
  const [camera, setCamera] = useState({
    x: 0,
    y: 0,
    scale: 1,
    canvasRect: null,
  });
  const cameraRef = useRef(camera);

  useEffect(() => {
    let frame;
    const tick = () => {
      const api = canvasApiRef && canvasApiRef.current;
      if (api) {
        const next = api.getCameraState();
        const prev = cameraRef.current;
        if (
          next.x !== prev.x ||
          next.y !== prev.y ||
          next.scale !== prev.scale ||
          rectChanged(next.canvasRect, prev.canvasRect)
        ) {
          cameraRef.current = next;
          setCamera(next);
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [canvasApiRef]);

  return camera;
};
